import React, { useState, useEffect } from 'react';
import API from '../../../pages/Api';
import { toast } from 'react-toastify';
import { FaEdit, FaPlus, FaTimes, FaTrash, FaCheckCircle, FaTimesCircle } from 'react-icons/fa';
import TableContainer from '../../common/Tablecontainer'; 

const ExpandedArticleDescCell = ({ text }) => {
    const [isExpanded, setIsExpanded] = useState(false);

    if (!text) return <span className="text-gray-400 italic">No description provided</span>;

    const shouldTruncate = text.length > 150;

    return (
        <div className="p-3 text-left pl-5 font-normal text-gray-600 max-w-xl leading-relaxed text-justify">
            <div className={!isExpanded && shouldTruncate ? "line-clamp-3 overflow-hidden" : "whitespace-pre-wrap break-words"}>
                {text}
            </div>
            {shouldTruncate && (
                <button
                    type="button"
                    onClick={() => setIsExpanded(!isExpanded)}
                    className="mt-1 text-blue-600 hover:text-blue-900 font-bold bg-transparent border-none cursor-pointer text-[11px] block tracking-normal transition-all duration-300 ease-in-out active:scale-95"
                >
                    {isExpanded ? '... Show less' : '... Show more'}
                </button>
            )}
        </div>
    );
};

const ArticleMaster = () => {
    const [articles, setArticles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    
    // Action modal operational states
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [selectedArticleId, setSelectedArticleId] = useState(null);
    const [mobilePage, setMobilePage] = useState(1);
    const [mobileTotal, setMobileTotal] = useState(0);
    const [loadingCards, setLoadingCards] = useState(false);
    const [list, setList] = useState([]);

    // Controlled form operational parameter states fields
    const [formData, setFormData] = useState({
        articletitle: '',
        articledesc: '',
        status: 'Active'
    });

    const loadMobileArticles = async (page = 1, search = "") => {

        try {

            setLoadingCards(true);

            const res = await API.get(
                `blog-articles/?page=${page}&search=${search}`
            );

            if (res.data.results) {

                setList(res.data.results);
                setMobileTotal(res.data.count);

            } else {

                setList(res.data);
                setMobileTotal(res.data.length);

            }

        } catch (err) {

            console.error(err);

            setList([]);
            setMobileTotal(0);

        } finally {

            setLoadingCards(false);

        }

    };
    useEffect(() => {

        loadMobileArticles(
            mobilePage,
            searchQuery
        );

    }, [mobilePage, searchQuery]);

    // 💡 Status Toggle Logic (New API Integration)
    const toggleStatus = async (item) => {
        const newStatus = item.status === 'Active' ? 'Inactive' : 'Active';
        try {
            await API.post('InactiveArticleDtls/', {
                articleid: item.articleid,
                status: newStatus
            });
            await loadMobileArticles(
                mobilePage,
                searchQuery
            );

            if (window.activeTableRefresh) {
                window.activeTableRefresh();
            }
        } catch (err) {
            console.error("Failure processing toggle status:", err);
            toast.error("Status update failed.");
        }
    };

    const openAddModalWindow = (refreshTable) => {
        setFormData({ articletitle: '', articledesc: '', status: 'Active' });
        setIsEditMode(false);
        setIsModalOpen(true);
        window.activeTableRefresh = refreshTable;
    };

    const openEditModalWindow = (item,refreshTable) => {
        setFormData({
            articletitle: item.articletitle,
            articledesc: item.articledesc,
            status: item.status || 'Active'
        });
        setSelectedArticleId(item.articleid);
        setIsEditMode(true);
        setIsModalOpen(true);
        window.activeTableRefresh = refreshTable;
    };

    const handleFormSubmitAction = async (e) => {
        e.preventDefault();
        if (!formData.articletitle || !formData.articledesc) {
            toast.warn("Please complete required input parameters configurations.");
            return;
        }

        try {
            if (isEditMode) {
                const res = await API.put(`blog-articles/${selectedArticleId}/`, formData);
                if (res.data.status === "Success") toast.success("Article configuration updated smoothly.");
            } else {
                const res = await API.post('blog-articles/', formData);
                if (res.data.status === "Success") toast.success("New article row synchronized inside database ledger.");
            }
            setIsModalOpen(false);

            await loadMobileArticles(
                mobilePage,
                searchQuery
            );

            if (window.activeTableRefresh) {
                window.activeTableRefresh();
            }
        } catch (err) {
            console.error("Operational crash executing submit payload mapping:", err);
        }
    };

    const handleRecordDeleteTrigger = async (pk) => {
        if (!window.confirm("Are you completely sure you want to delete this specific article record?")) return;
        try {
            const res = await API.delete(`blog-articles/${pk}/`);
            if (res.data.status === "Success") {
                toast.success("Article removed successfully.");
                await loadMobileArticles(
                    mobilePage,
                    searchQuery
                );

                if (window.activeTableRefresh) {
                    window.activeTableRefresh();
                }
            }
        } catch (err) {
            console.error("Failure processing delete entity instruction:", err);
        }
    };
    const MobileArticleDesc = ({ text }) => {

        const [show, setShow] = useState(false);

        if (!text) return null;

        const longText = text.length > 120;

        return (

            <>

                <p
                    className={`text-sm break-all ${
                        show ? "" : "line-clamp-3"
                    }`}
                >
                    {text}
                </p>

                {longText && (

                    <button
                        onClick={() => setShow(!show)}
                        className="text-blue-600 text-xs mt-1"
                    >
                        {show ? "Read Less" : "Read More"}
                    </button>

                )}

            </>

        );

    };

    // const filteredArticles = articles.filter(item => 
    //     item.articletitle?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    //     item.articledesc?.toLowerCase().includes(searchQuery.toLowerCase())
    // );

    const gridHeaders = ['Article Title', 'Article Description', 'Article Date', 'Status', 'Action'];

    return (
        <div className="p-6 bg-[#F8FAFC] min-h-screen w-full flex flex-col items-center font-sans">
            <div className="w-full max-w-6xl mx-auto flex flex-col space-y-4 px-2">
                
                {/* ─── PAGE HEADING TITLE ─── */}
                <div className="w-full py-3.5 text-center rounded-lg">
                    <h2 className="text-center text-4xl font-light text-gray-700 mb-8 tracking-tight">
                        Blog Articles
                    </h2>
                </div>

                {/* ─── ACTION CONTAINER ─── */}
                <div className="w-full px-1 py-2.5 rounded-lg flex justify-end items-center">
                    <button 
                        type="button"
                        onClick={() =>
                        openAddModalWindow(
                        window.activeTableRefresh
                        )
                        }
                        className="bg-[#032167] text-white px-5 py-2 rounded text-xs font-semibold tracking-wide flex items-center gap-2 hover:bg-blue-950 transition-all border-none cursor-pointer shadow-sm active:scale-95"
                    >
                        <FaPlus size={10} /> Add New Article
                    </button>
                </div>
                <div className="hidden md:block">
                <TableContainer
                    columns={gridHeaders}
                    apiUrl="blog-articles/"
                    searchQuery={searchQuery}
                    setSearchQuery={(value) => {

                        setSearchQuery(value);

                        setMobilePage(1);

                    }} 
                    searchPlaceholder="Search articles by title or content parameters..."
                   
                    renderRow={(item, index, srNo, formatCurrency, refreshTable) => (
                        <tr key={item.articleid || index} className="border-b border-gray-200 hover:bg-gray-50 transition-colors h-auto text-gray-700 text-xs font-medium bg-white">
                            <td className="p-3 border-r bg-gray-50/50 font-bold text-gray-400 w-[6%] text-center select-none">{srNo}</td>
                            <td className="p-3 border-r text-left pl-5 w-[20%] uppercase font-bold text-[#032167] tracking-wide">{item.articletitle}</td>
                            <td className="p-0 border-r w-[46%] vertical-align-top">
                                <ExpandedArticleDescCell text={item.articledesc} />
                            </td>
                            <td className="p-3 border-r font-medium text-gray-500 w-[12%] text-center whitespace-nowrap">{item.article_date || 'N/A'}</td>
                            
                            {/* Status Icon Toggle */}
                            <td className="p-3 border-r w-[8%] text-center">
                                <div className="flex items-center justify-center w-full h-full cursor-pointer" onClick={() => toggleStatus(item, refreshTable)}>
                                    {item.status === 'Active' || item.status === 'A' ? (
                                        <FaCheckCircle size={18} className="text-emerald-500" title="Active Status" />
                                    ) : (
                                        <FaTimesCircle size={18} className="text-rose-400" title="Inactive Status" />
                                    )}
                                </div>
                            </td>
                            
                            {/* Action columns */}
                            <td className="w-[12%] p-0">
                                <div className="flex items-center justify-center w-full h-[44px]">
                                    <div className="flex-1 flex justify-center items-center h-full border-r border-gray-200">
                                        <button 
                                            type="button"
                                            onClick={() => openEditModalWindow(item, refreshTable)} // 🟢 refreshTable पास केला
                                            className="text-[#5c9314] hover:text-green-800 border-none bg-transparent cursor-pointer transition-all flex items-center justify-center w-full h-full"
                                            title="Edit Article Content"
                                        >
                                            <FaEdit size={18} />
                                        </button>
                                    </div>
                                    <div className="flex-1 flex justify-center items-center h-full">
                                        <button 
                                            type="button"
                                            onClick={() => handleRecordDeleteTrigger(item.articleid, refreshTable)} // 🟢 refreshTable पास केला
                                            className="text-[#b0b7bd] hover:text-red-600 border-none bg-transparent cursor-pointer transition-all flex items-center justify-center w-full h-full"
                                            title="Delete Article Entry"
                                        >
                                            <FaTrash size={16} />
                                        </button>
                                    </div>
                                </div>
                            </td>
                        </tr>
                    )}
                />
                </div>

            </div>
            <div className="md:hidden mt-4 space-y-4">

                {loadingCards ? (

                    <div className="text-center py-10">
                        Loading...
                    </div>

                ) : list.length === 0 ? (

                    <div className="text-center py-10">
                        No Articles Found
                    </div>

                ) : (

                    list.map((item) => (

                        <div
                            key={item.articleid}
                            className="w-full
                            max-w-full
                            min-w-0
                            bg-white
                            rounded-xl
                            border
                            border-gray-200
                            shadow-sm
                            p-4
                            overflow-hidden"
                        >

                            <div className="space-y-3 w-full min-w-0 overflow-hidden">

                                <div>

                                    <p className="text-xs text-gray-500">
                                        Article Title
                                    </p>

                                    <p className="font-bold text-[#032167]">
                                        {item.articletitle}
                                    </p>

                                </div>

                                <div>

                                    <p className="text-xs text-gray-500">
                                        Description
                                    </p>

                                    <MobileArticleDesc
                                        text={item.articledesc}
                                    />

                                </div>

                                <div className="flex justify-between">

                                    <div>

                                        <p className="text-xs text-gray-500">
                                            Date
                                        </p>

                                        <p>
                                            {item.article_date || "N/A"}
                                        </p>

                                    </div>

                                    <div>

                                        <p className="text-xs text-gray-500">
                                            Status
                                        </p>

                                        <p>
                                            {item.status}
                                        </p>

                                    </div>

                                </div>

                                <div className="border-t pt-3 flex justify-end gap-4">

                                    <button
                                        onClick={() =>
                                            openEditModalWindow(item)
                                        }
                                    >
                                        <FaEdit
                                            className="text-green-600"
                                        />
                                    </button>

                                    <button
                                        onClick={() =>
                                            handleRecordDeleteTrigger(
                                                item.articleid
                                            )
                                        }
                                    >
                                        <FaTrash
                                            className="text-red-600"
                                        />
                                    </button>

                                </div>

                            </div>

                        </div>

                    ))

                )}

            </div>
            <div className="md:hidden flex justify-between items-center mt-5">

                <button
                    disabled={mobilePage === 1}
                    onClick={() =>
                        setMobilePage((p) => p - 1)
                    }
                >
                    Previous
                </button>

                <span>

                    {mobilePage} / {Math.ceil(mobileTotal / 10)}

                </span>

                <button
                    disabled={
                        mobilePage >=
                        Math.ceil(mobileTotal / 10)
                    }
                    onClick={() =>
                        setMobilePage((p) => p + 1)
                    }
                >
                    Next
                </button>

            </div>

            {/* --- Modal Panel --- */}
            {isModalOpen && (
                <div className="fixed
                inset-0
                z-[9999]
                bg-black/60
                flex
                items-center
                justify-center
                p-3
                overflow-hidden">
                    <div
                    className="
                    relative
                    bg-white
                    w-full
                    max-w-lg
                    rounded-2xl
                    shadow-2xl
                    flex
                    flex-col
                    overflow-hidden
                    max-h-[90vh]
                    "
                    >
                        <div
                        className="
                        bg-[#032167]
                        px-5
                        py-4
                        flex
                        justify-between
                        items-center
                        rounded-t-2xl
                        "
                        >
                            <h3 className="text-sm font-bold text-white uppercase tracking-wider">
                                {isEditMode ? 'Modify Article Asset Form' : 'Add/Edit new article'}
                            </h3>
                            <button type="button" onClick={() => setIsModalOpen(false)} className="text-white
                            hover:text-gray-200
                            border-none
                            bg-transparent
                            text-xl
                            cursor-pointer
                            font-bold">
                                <FaTimes size={16} />
                            </button>
                        </div>

                        <form
                        onSubmit={handleFormSubmitAction}
                        className="
                        overflow-y-auto
                        max-h-[70vh]
                        p-5
                        space-y-5
                        text-xs
                        font-semibold
                        uppercase
                        text-gray-700
                        "
                        >
                            <div className="flex flex-col space-y-1.5">
                                <label>Article Heading Title <span className="text-red-500">*</span></label>
                                <input type="text" value={formData.articletitle} onChange={(e) => setFormData({...formData, articletitle: e.target.value})} placeholder="Type article main headline name..." className="p-2.5 border border-gray-300 rounded outline-none bg-gray-50 font-bold text-gray-800 normal-case" required />
                            </div>

                            <div className="flex flex-col space-y-1.5">
                                <label>Article Core Content Paragraph Text <span className="text-red-500">*</span></label>
                                <textarea rows={5} value={formData.articledesc} onChange={(e) => setFormData({...formData, articledesc: e.target.value})} placeholder="Type full comprehensive article details text descriptions here..." className="p-2.5 border border-gray-300 rounded outline-none bg-gray-50 font-medium normal-case text-gray-700 resize-none leading-relaxed" required />
                            </div>

                            <div className="flex flex-col space-y-1.5 w-full sm:max-w-xs">
                                <label>Active Release Visibility Status</label>
                                <select value={formData.status} onChange={(e) => setFormData({...formData, status: e.target.value})} className="p-2.5 border border-gray-300 rounded outline-none bg-gray-50 font-bold">
                                    <option value="Active">Active State</option>
                                    <option value="Inactive">Inactive State</option>
                                </select>
                            </div>

                            <div
                            className="
                            pt-5
                            border-t
                            flex
                            flex-col
                            gap-3
                            "
                            >
                                <button type="button" onClick={() => setIsModalOpen(false)} className="w-full
                                    sm:w-auto
                                    px-5
                                    py-2
                                    bg-gray-100
                                    text-gray-700
                                    rounded
                                    font-bold
                                    uppercase
                                    tracking-wider
                                    border
                                    hover:bg-gray-200
                                    cursor-pointer">Cancel</button>
                                <button type="submit" className="
                                w-full
                                bg-gray-100
                                py-3
                                rounded-xl
                                font-bold
                                ">Save Article</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ArticleMaster;